import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { LayersIcon, LockIcon, PlusIcon, SearchIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/EmptyState';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { AccountMenu } from '@/shared/components/AccountMenu';
import { displayNameOf } from '@/shared/components/accountIdentity';
import { BrandMark } from '@/shared/components/BrandMark';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { IdentityTexture } from '@/shared/components/IdentityTexture';
import { accentFor } from '@/shared/components/identityAccent';
import { isTextEntry } from '@/shared/utils/isTextEntry';
import { formatUpdated, greeting, workspaceInitial } from '../display';
import { useCurrentUser } from '@/features/identity';
import { ACCESS_COPY, VISIBILITY_NAME } from '../accessCopy';
import { workspaceMarks } from '../marks';
import { usePreferencesStore } from '@/shared/store/preferences';
import { useWorkspaceList } from '../hooks/useWorkspaces';
import type { WorkspaceTab } from '../searchParams';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { JUMP_BACK_IN_LIMIT, JumpBackIn } from './JumpBackIn';
import { WorkspaceTabs } from './WorkspaceTabs';
import { WorkspaceSortMenu, type WorkspaceSort } from './WorkspaceSortMenu';

/** One page as the list asks for it, and the largest the contract allows. */
const PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;

/** When anything last happened in the workspace, from the service; its own edit when not sent. */
function lastActive(entry: {
  updated_at: string;
  last_activity_at?: string | null;
}) {
  return entry.last_activity_at ?? entry.updated_at;
}

/**
 * The application root.
 *
 * A hero surface under the brand rules, which is why it does not use the shared
 * page composition — that composition is for the dense application layouts
 * inside a workspace.
 *
 * The list comes from the service, so a workspace is reachable from any machine
 * by anyone, and one request renders it however many entries it holds. It was
 * previously built from identifiers opened on this device, which made a
 * workspace unreachable the moment you opened it somewhere else.
 */
export interface WorkspaceListPageProps {
  /**
   * Whether the create dialog is open — owned by the address, not by this
   * component (`?create=true`).
   *
   * It lives there because the command palette has to be able to open it: the
   * palette deliberately opens no dialog of its own (one dialog, one owner), so
   * a link is the only way it can ask. Once the address can say "open", having a
   * second copy of that fact in local state would mean the two could disagree.
   *
   * The first version of this kept the state here and synchronised it from a
   * prop in an effect, which is the shape React's own guidance and this repo's
   * lint rule both reject — `setState` inside an effect body cascades renders,
   * and the effect had to be defended against re-running on every navigation.
   * Deriving is simpler than synchronising.
   */
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
  /**
   * What the bar carries before the account menu: the bell, as every shell's
   * bar has. Passed in by the route rather than imported here, because
   * notifications imports `workspaces`, and importing it back would close a
   * cycle.
   */
  headerEnd?: ReactNode;
  /**
   * The open tab once access divides the list (spec 200), owned by the address
   * (`?tab=`) for the same reason `createOpen` is: a link can name it, and Back
   * returns to it.
   */
  tab?: WorkspaceTab;
  onTabChange?: (tab: WorkspaceTab) => void;
}

export function WorkspaceListPage({
  createOpen,
  onCreateOpenChange,
  headerEnd,
  tab,
  onTabChange = () => undefined,
}: WorkspaceListPageProps) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  // See the note in `WorkflowListPage`: a view transition is not a style rule,
  // so the preference is read here rather than left to the stylesheet.
  const reducedMotion = useReducedMotion();
  const workspaceList = useWorkspaceList({ limit });
  // A placeholder only once the wait is worth remarking on (spec 079 FR-017).
  const showPlaceholder = usePendingDisplay(workspaceList.isPending);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<WorkspaceSort>('updated');
  const phone = usePhoneWidth();
  const searchRef = useRef<HTMLInputElement>(null);

  // `/` focuses the search from anywhere on the page (spec 199 US1), as it does
  // on the Tasks page — never while typing somewhere else, and never with a
  // modifier held, which belongs to the browser.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (isTextEntry(event.target) || searchRef.current === null) return;
      event.preventDefault();
      searchRef.current.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // One clock for the whole render, so two rows a millisecond apart cannot
  // disagree about what "today" is.
  const now = new Date();
  /*
    Divided into sections once access is on (spec 193), and a section built from
    the first page alone says "yours" of only the first 50 — the rest of yours
    sorted, unseen, below a "Show more" that belongs to the flat list. So when
    the first page says the list is divided and holds less than all of it, the
    page reads the whole list, up to the largest page the contract allows, and
    draws from that once it lands.
  */
  const firstPage = workspaceList.data;
  const sectioned =
    firstPage?.items.some((entry) => entry.access?.enforced === true) ?? false;
  const readsAll =
    sectioned &&
    firstPage !== undefined &&
    firstPage.total > firstPage.items.length;
  const everyWorkspace = useWorkspaceList({
    limit: MAX_PAGE_SIZE,
    enabled: readsAll,
  });
  const page =
    readsAll && everyWorkspace.data ? everyWorkspace.data : firstPage;
  const workspaces = page?.items ?? [];
  const undisplayed = page ? page.total - page.items.length : 0;

  // Both run over the array the page already holds — the list is one request,
  // so ordering and narrowing it is never a second one.
  const needle = query.trim().toLowerCase();
  // Where this person was last, on this device (spec 122 follow-up). The
  // store holds ids; the cards are the list's own entries for them, in the
  // order they were opened, so a workspace that has since gone is simply not
  // shown. Not while the list is narrowed: a narrowed list is already the
  // reader saying which ones they mean.
  const recentWorkspaces = usePreferencesStore(
    (state) => state.recentWorkspaces,
  );
  const currentUser = useCurrentUser();
  const recent =
    needle === ''
      ? recentWorkspaces
          .map((visit) => {
            const entry = workspaces.find((item) => item.id === visit.id);
            return entry ? { entry, openedAt: visit.openedAt } : null;
          })
          .filter((visit) => visit !== null)
          .slice(0, JUMP_BACK_IN_LIMIT)
      : [];
  const visible = workspaces
    .filter((entry) => entry.name.toLowerCase().includes(needle))
    .sort((a, b) =>
      sort === 'name'
        ? a.name.localeCompare(b.name)
        : // Most recently active first: the order the service already sent,
          // restated so a narrowed list keeps it. Activity, not `updated_at`,
          // which moves only when the workspace itself is edited — work on its
          // tasks never touched it.
          lastActive(b).localeCompare(lastActive(a)),
    );

  // One card grid, drawn once per tab (spec 188, spec 200).
  const grid = (entries: typeof visible) => (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {entries.map((entry) => {
        const marks = workspaceMarks(entry, currentUser.data?.id);
        const active = formatUpdated(lastActive(entry), now, 'Active');
        // Your own name under every one of your workspaces said nothing; the
        // creator is worth a line only when it is someone else (spec 201).
        const creator =
          entry.created_by && entry.created_by.id !== currentUser.data?.id
            ? displayNameOf(
                entry.created_by.display_name,
                entry.created_by.email,
              )
            : null;
        const role = marks.owner
          ? 'Owner'
          : marks.viewOnly
            ? ACCESS_COPY.viewOnlyChip
            : null;
        return (
          <li
            key={entry.id}
            className={cn(
              'group border-border bg-card relative flex flex-col gap-2.5 rounded-xl border p-3.5',
              // Colour and one step of elevation (spec 122 FR-014). No entrance
              // animation: `animate-in` replayed on every visit and every
              // skeleton swap, so a list you knew blinked in each time.
              'hover:border-primary/40 hover:bg-accent/40 hover:shadow-raised transition-[border-color,background-color,box-shadow] motion-safe:duration-[var(--motion-duration-base)]',
            )}
          >
            <div className="flex min-w-0 items-start gap-3">
              {/* The monogram is what carries into the shell: the rail shows
                  the same letter for the same workspace, so the two are the
                  same object seen twice (spec 075 FR-026). Named from the id,
                  never the position (FR-028). */}
              <span
                aria-hidden="true"
                style={{ viewTransitionName: `workspace-${entry.id}` }}
                className={cn(
                  'relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg text-sm font-semibold',
                  accentFor(entry.id),
                )}
              >
                <IdentityTexture id={entry.id} />
                <span className="relative">{workspaceInitial(entry.name)}</span>
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex min-w-0 items-start gap-1.5">
                  {/* Stretched over the card with a pseudo-element, so the
                      whole tile is one target. Two lines, then an ellipsis,
                      and the whole name on hover: names here are often long
                      on purpose (E2E fixtures suffix a timestamp), and one
                      line loses the part that tells two apart. */}
                  <Link
                    to="/workspaces/$workspaceId"
                    params={{ workspaceId: entry.id }}
                    viewTransition={!reducedMotion}
                    className="focus-visible:ring-ring block min-w-0 rounded-md after:absolute after:inset-0 after:rounded-xl focus-visible:ring-3 focus-visible:outline-none"
                  >
                    <span
                      className="text-foreground line-clamp-2 block font-medium break-words"
                      title={entry.name}
                    >
                      {entry.name}
                    </span>
                  </Link>
                  {marks.private ? (
                    <span className="text-muted-foreground mt-1 shrink-0">
                      <LockIcon
                        aria-hidden="true"
                        strokeWidth={1.5}
                        className="size-3.5"
                      />
                      <span className="sr-only">{VISIBILITY_NAME.private}</span>
                    </span>
                  ) : null}
                </div>
                {active !== null || creator !== null ? (
                  <span className="text-muted-foreground truncate text-xs">
                    {[active, creator === null ? null : `by ${creator}`]
                      .filter((part) => part !== null)
                      .join(' · ')}
                  </span>
                ) : null}
              </div>
            </div>

            {/* Where you stand and what it holds, on one line (spec 201): the
                role as a word rather than a chip, then the counts from spec
                070's opt-in enrichment, which are optional on the contract
                and so render only when present. The workflow names left the
                card — cut at three, they were the line that made it tall. */}
            {role !== null || entry.counts ? (
              // On a phone it starts at the name's edge, not the monogram's:
              // one left edge for every line of text in the card (spec 267).
              <p className="text-muted-foreground text-xs tabular-nums max-md:ps-12">
                {role !== null ? (
                  <span className="text-foreground">{role}</span>
                ) : null}
                {role !== null && entry.counts ? ' · ' : null}
                {entry.counts
                  ? `${String(entry.counts.workflows)} ${
                      entry.counts.workflows === 1 ? 'workflow' : 'workflows'
                    } · ${String(entry.counts.tasks)} ${
                      entry.counts.tasks === 1 ? 'task' : 'tasks'
                    }`
                  : null}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    /*
      This page scrolls itself, rather than letting the document scroll.

      `ShellFrame` reserves a scrollbar gutter on its own `<main>`, which is why
      the board and every workspace page stopped shifting sideways — but the
      workspace *list* has no shell: it is the front door, as the note below
      says, so it was never covered. Filtering the list crosses the viewport
      height, the document's scrollbar comes and goes, and every card jumps by
      its width while you are typing.

      The gutter cannot simply go on `html`: measured in Chrome, that reserves
      15px whether or not a scrollbar is showing, and `ShellFrame` is
      `h-screen overflow-hidden` — so every shell page would gain a permanent
      dead strip to fix a page that is not in the shell. Giving this page its
      own scroll container keeps the fix where the problem is.
    */
    <div className="bg-background scrollbar-gutter-stable flex h-screen flex-col overflow-auto">
      {/* The control this page was missing: signed in, with no way to sign out.
          `SignedInPerson` has existed the whole time, but it is mounted in the
          workspace sidebar, and that sidebar is meaningless here — its contents
          are a workspace switcher and a board list. So the account chip comes to
          the front door instead of the shell coming with it.

          A real `header`, which makes this the page's banner landmark. That is
          correct here and only here: this page has no shell above it, so there
          is no second banner for it to compete with. */}
      {/* Sticky, because it was not: the account control scrolled away with the
          page, so the one way to sign out disappeared the moment you looked at
          your workspaces. Translucent with a blur so the list reads as passing
          under it rather than being clipped by a slab.

          Identity on the left, controls on the right. The left reads like the
          sidebar's product mark on every other page, the spade then the
          product's name, with the programme it belongs to after it ("part of
          RAISE for Software Product X", dropped below `sm` so the bar never
          wraps). The right holds the bell and the account menu. The RAISE line
          sits on the left here, not at the bar's end as in the shells: there
          the account lives in the sidebar, so the line ends the bar; here the
          account menu does. */}
      <header className="border-border/60 surface-veil sticky top-0 z-40 flex shrink-0 items-center justify-between gap-4 border-b px-4 py-2.5 sm:px-8">
        <span className="flex min-w-0 items-center gap-2.5">
          <span role="img" aria-label="Capgemini" className="shrink-0">
            <BrandMark
              variant="spade"
              className="text-primary dark:text-foreground size-6"
            />
          </span>
          <span className="text-muted-foreground truncate text-sm">
            AI-Powered SDLC
            <span className="hidden sm:inline">
              {' · '}part of RAISE for Software Product X
            </span>
          </span>
        </span>
        <div className="flex shrink-0 items-center gap-3">
          {headerEnd}
          <AccountMenu />
        </div>
      </header>

      {/* 16px on a phone, the gutter every other page has (spec 267). */}
      <main className="flex flex-1 flex-col items-center px-4 pb-16 sm:px-10">
        <div className="flex w-full max-w-7xl flex-col gap-8 pt-8 max-md:gap-6 max-md:pt-6">
          {/* The hero, given the room FD-17 licenses on this one surface. The
              logo is at its sanctioned size for the first time — it was an
              `<img>` 28px tall, so roughly 73px of artwork against a 175px
              floor, and rendering black besides, because `currentColor` inside
              an `<img>` cannot see the page. */}
          <div className="flex flex-col items-start gap-5 max-md:gap-4">
            {/* The logo stays, and stays this size, for two reasons: FD-18's
                175px floor applies to the artwork, and the spade in the bar above
                is only permitted *because* the full logo appears on the page. It
                is the one element here that cannot be shrunk for density. */}
            <BrandMark flush className="text-primary" />
            <div className="flex w-full flex-wrap items-end justify-between gap-x-8 gap-y-4">
              <div className="flex min-w-0 flex-col gap-1.5">
                {/* Light per FD-14 — Regular reads heavy in a headline — but
                    3xl rather than 5xl: this is a page you pass through on the
                    way somewhere, and the title was taking the room the list
                    should have. */}
                <h1 className="text-foreground text-3xl font-light tracking-tight">
                  Workspaces
                </h1>
                <p className="text-muted-foreground text-sm">
                  {/* The page opens by addressing the person, not by announcing
                      a list. First name only; nothing while identity loads.
                      The instruction that followed it ("choose one to open it,
                      or create a new one") said what the page plainly shows. */}
                  {greeting(now, currentUser.data?.display_name)}
                </p>
              </div>
              {/* The page's controls, on the title's row (spec 199 US1): above
                  everything they act on. The search used to sit between the
                  recent row and the grid, where it read as a divider, filtered
                  only what was under it, and jumped up mid-keystroke when the
                  recent row hid. */}
              {workspaces.length > 0 ? (
                // On a phone: search and Sort on one row, New workspace full
                // width under them (spec 267). Side by side, the field was too
                // narrow for its own placeholder.
                <div className="flex w-full items-center gap-2 max-md:flex-wrap md:w-auto">
                  {/* The icon sits inside the field rather than beside it, so
                      the control reads as one object. */}
                  <div className="relative min-w-0 flex-1 md:w-90 md:flex-none">
                    <SearchIcon
                      aria-hidden="true"
                      className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                      strokeWidth={1.5}
                    />
                    <Input
                      ref={searchRef}
                      aria-label="Filter workspaces"
                      aria-keyshortcuts="/"
                      placeholder="Search workspaces"
                      value={query}
                      onChange={(event) => {
                        setQuery(event.target.value);
                      }}
                      className="h-10 w-full pl-9 text-sm sm:pr-9"
                    />
                    {/* The shortcut, said where it applies — only while it
                        would do something, and not on a phone, which has no
                        key for it and needs the room for the placeholder.
                        Assistive technology reads `aria-keyshortcuts`. */}
                    {query === '' ? (
                      <kbd
                        aria-hidden="true"
                        className="border-border text-muted-foreground bg-muted pointer-events-none absolute top-1/2 right-2.5 hidden -translate-y-1/2 rounded border px-1.5 font-mono text-xs leading-4 sm:block"
                      >
                        /
                      </kbd>
                    ) : null}
                  </div>
                  {phone ? (
                    <WorkspaceSortMenu
                      sort={sort}
                      onSortChange={setSort}
                      icon
                    />
                  ) : null}
                  <Button
                    size="lg"
                    className="shrink-0 max-md:w-full"
                    onClick={() => {
                      onCreateOpenChange(true);
                    }}
                  >
                    <PlusIcon aria-hidden="true" />
                    New workspace
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          {/*
            Where you were (spec 122 follow-up): the workspaces this person
            opened last on this device, as a row above the full list. Personal
            rather than "most recently active" — the sort control already
            answers that, and a row that repeated it in smaller boxes said
            nothing the grid did not. Hidden until something has been opened,
            and while the list is narrowed.
          */}
          {recent.length > 0 ? (
            <JumpBackIn visits={recent} currentUserId={currentUser.data?.id} />
          ) : null}

          {/*
          Four states, and the last two must not be conflated: a failed request
          and an empty collection look identical to a person unless the UI says
          which one it is.
        */}
          {workspaceList.isPending ? (
            showPlaceholder && (
              // Card-shaped placeholders, not the word "Loading". This is the
              // front door of the product, and a dashed box with a sentence in
              // it reads as an error rather than as a wait — the one state
              // this page most needed to keep distinct.
              <PendingRegion label="Loading workspaces" className="gap-3">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    data-testid="workspace-card-skeleton"
                    aria-hidden="true"
                    className="border-border flex items-center gap-3 rounded-lg border p-4"
                  >
                    <Skeleton className="size-9 shrink-0 rounded-md" />
                    <span className="flex min-w-0 flex-1 flex-col gap-2">
                      <Skeleton className="h-3.5 w-48 max-w-full rounded-sm" />
                      <Skeleton className="h-3 w-32 max-w-full rounded-sm" />
                    </span>
                  </div>
                ))}
              </PendingRegion>
            )
          ) : workspaceList.isError ? (
            // The shared panel, kept as an alert and kept on the destructive
            // border: a failed read on the front door is the one state a reader
            // must not confuse with an empty account, which is what the two
            // sentences below are for.
            <EmptyState
              role="alert"
              className="border-destructive/40 flex-none"
              icon={LayersIcon}
              title="Workspaces could not be loaded"
              description={
                <>
                  This is not an empty list — the service could not be reached,
                  so there may well be workspaces waiting.
                </>
              }
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void workspaceList.refetch()}
                >
                  Try again
                </Button>
              }
            />
          ) : workspaces.length === 0 ? (
            <EmptyState
              className="flex-none"
              icon={LayersIcon}
              description="No workspaces yet. A workspace is the shared boundary for files, workflows, agents, and the tasks that run against them."
              // The action belongs in the box that just referred to it. It used
              // to be a separate form below, competing with its own explanation.
              action={
                <Button
                  onClick={() => {
                    onCreateOpenChange(true);
                  }}
                >
                  Create your first workspace
                </Button>
              }
            />
          ) : (
            <div className="flex flex-col gap-3">
              {/* A filter that matches nothing is not an empty collection, and
                must not borrow the empty state's invitation to create a first
                workspace — there are workspaces here, just not these. */}
              {visible.length === 0 ? (
                <EmptyState
                  className="flex-none"
                  icon={LayersIcon}
                  description="No workspaces match this filter."
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setQuery('');
                      }}
                    >
                      Clear filter
                    </Button>
                  }
                />
              ) : (
                <WorkspaceTabs
                  all={workspaces}
                  entries={visible}
                  grid={grid}
                  currentUserId={currentUser.data?.id}
                  query={query}
                  tab={tab}
                  onTabChange={onTabChange}
                  // How many, and in what order (spec 199 US3) — the count only
                  // while there are no tabs to carry it (spec 200).
                  count={
                    <p className="text-muted-foreground text-xs tabular-nums">
                      {needle === ''
                        ? `${String(workspaces.length)} ${workspaces.length === 1 ? 'workspace' : 'workspaces'}`
                        : `Showing ${String(visible.length)} of ${String(workspaces.length)}`}
                    </p>
                  }
                  sortMenu={
                    phone ? null : (
                      <WorkspaceSortMenu sort={sort} onSortChange={setSort} />
                    )
                  }
                />
              )}
              {/*
              The service caps a page, so say so rather than presenting a
              truncated list as if it were everything. `total` exists precisely
              so this can be known without asking for another page.
            */}
              {undisplayed > 0 ? (
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <p className="text-muted-foreground text-xs tabular-nums">
                    Showing {workspaces.length} of {page?.total} workspaces.
                  </p>
                  {/* The message used to end here, naming a gap it gave no way
                    to cross. Past the contract's largest page it goes back to
                    naming it — an offer that cannot be honoured is worse than
                    none. */}
                  {/* The flat list's; sections already read all they can, unless
                    that read failed. */}
                  {(!sectioned || everyWorkspace.isError) &&
                  limit < MAX_PAGE_SIZE ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={workspaceList.isFetching}
                      onClick={() => {
                        setLimit((current) =>
                          Math.min(current + PAGE_SIZE, MAX_PAGE_SIZE),
                        );
                      }}
                    >
                      {workspaceList.isFetching ? 'Loading…' : 'Show more'}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <CreateWorkspaceDialog
          open={createOpen}
          onOpenChange={onCreateOpenChange}
        />
      </main>
    </div>
  );
}
